<template>
  <div class="table-container">
    <div class="table-header">
      <div class="table-title">
        {{$t('user')}} ({{ filteredUsers.length }})
      </div>
      <div class="table-actions">
        <button
            v-if="showViewAll && filteredUsers.length > 3"
            @click="toggleExpanded"
            class="view-all-btn"
        >
          {{ isExpanded ? $t('show_less') : $t('show_more') }}
        </button>
      </div>
    </div>

    <div class="desktop-table" :class="{ 'expanded': isExpanded }">
      <table class="content-table">
        <thead class="header-table">
        <tr class="trhead">
          <th class="rank-header">{{ $t('leadership') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('rank')"
            >
          </th>
          <th @click="sortBy('name')" :class="getSortClass('name')">
            {{ $t('user') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('name')"
            >
          </th>
          <th @click="sortBy('totalPoints')" :class="getSortClass('totalPoints')">
            {{ $t('total_points') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('totalPoints')"
            >
          </th>
          <th @click="sortBy('status')" :class="getSortClass('status')">
            {{ $t('status') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('status')"
            >
          </th>
          <th @click="sortBy('assignedPrograms')" :class="getSortClass('assignedPrograms')">
            {{ $t('assigned_programs') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('assignedPrograms')"
            >
          </th>
          <th @click="sortBy('completionRate')" :class="getSortClass('completionRate')">
            {{ $t('avg_completion_rate') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('completionRate')"
            >
          </th>
        </tr>
        </thead>

        <tbody class="tbody-container">
        <tr v-if="loading">
          <td colspan="6" class="table-loading">
            <div class="loading-content">
              <LottieAnimation
                  :animation-data="loadingAnim"
                  :auto-play="true"
                  :loop="true"
                  :speed="1"
                  class="lottie-loader"
              />
            </div>
          </td>
        </tr>

        <tr v-else-if="!users || users.length === 0">
          <td colspan="6" class="table-loading">
            <div class="empty-state">
              <span class="no-data-found">{{ $t('no_users_found') }}</span>
            </div>
          </td>
        </tr>

        <tr
            v-else
            v-for="(user, index) in displayedUsers"
            :key="user.id"
            class="tr-class"
        >
          <td class="td-style rank-cell">
            <span class="rank-number">{{ getRank(index) }}</span>
          </td>

          <td class="td-style td-row name-row">
            <div class="user-info">
              <img
                  :src="user.avatar || getDefaultAvatar(user.name)"
                  :alt="user.name"
                  class="user-avatar"
                  @click="handleUserClick(user)"
                  @error="handleImageError"
              />
              <div class="user-details">
                <span
                    class="table-link user-name"
                    @click="handleUserClick(user)"
                >
                  {{ user.name || $t('unnamed_user') }}
                </span>
                <div class="user-department">{{ user.department || $t('unknown') }}</div>
              </div>
            </div>
          </td>

          <td class="td-style">
            <div class="points-wrapper">
              <span class="points-text">{{ user.totalPoints || 0 }}</span>
            </div>
          </td>

          <td class="td-style">
            <div class="status-tag" :class="getStatusClass(user.status)">
              <div class="status-dot" :class="getStatusClass(user.status)"></div>
              <span>{{ getStatusText(user.status) }}</span>
            </div>
          </td>

          <td class="td-style">
            <div class="programs-wrapper">
              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.7109 4.75H17.1504C17.3493 4.75 17.5401 4.82902 17.6807 4.96967C17.8214 5.11032 17.9004 5.30109 17.9004 5.5V16C17.9004 16.1989 17.8214 16.3897 17.6807 16.5303C17.5401 16.671 17.3493 16.75 17.1504 16.75H3.65039C3.45148 16.75 3.26071 16.671 3.12006 16.5303C2.97941 16.3897 2.90039 16.1989 2.90039 16V4C2.90039 3.80109 2.97941 3.61032 3.12006 3.46967C3.26071 3.32902 3.45148 3.25 3.65039 3.25H9.21089L10.7109 4.75ZM4.40039 6.25V15.25H16.4004V6.25H4.40039Z" fill="#475467"/>
              </svg>
              <span class="test-class">{{ user.assignedPrograms || 0 }}</span>
            </div>
          </td>

          <td class="td-style">
            <div class="progress-wrapper">
              <div class="progress-bar">
                <div
                    class="progress-fill"
                    :style="{ width: `${user.completionRate || 0}%` }"
                    :class="getProgressClass(user.completionRate)"
                ></div>
              </div>
              <span class="test-class progress-text">{{ Math.round(user.completionRate || 0) }}%</span>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="mobile-cards">
      <div v-if="loading" class="mobile-loading">
        <div class="loading-content">
          <LottieAnimation
              :animation-data="loadingAnim"
              :auto-play="true"
              :loop="true"
              :speed="1"
              class="lottie-loader"
          />
        </div>
      </div>

      <div v-else-if="!users || users.length === 0" class="mobile-empty">
        <div class="empty-state">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="48" height="48" rx="24" fill="#F9FAFB"/>
            <path d="M24 16C22.9 16 22 16.9 22 18C22 19.1 22.9 20 24 20C25.1 20 26 19.1 26 18C26 16.9 25.1 16 24 16Z" fill="#667085"/>
            <path d="M24 22C21.79 22 20 23.79 20 26V30H28V26C28 23.79 26.21 22 24 22Z" fill="#667085"/>
          </svg>
          <span class="text">{{ $t('no_users_found') }}</span>
        </div>
      </div>

      <div
          v-else
          v-for="(user, index) in displayedUsers"
          :key="user.id"
          class="user-card"
          @click="handleUserClick(user)"
      >
        <div class="card-header">
          <div class="user-info">
            <div class="rank-badge">{{ getRank(index) }}</div>
            <img
                :src="user.avatar || getDefaultAvatar(user.name)"
                :alt="user.name"
                class="user-avatar"
                @error="handleImageError"
            />
            <div class="user-details">
              <h3 class="user-name">{{ user.name || $t('unnamed_user') }}</h3>
              <p class="user-department">{{ user.department || $t('unknown') }}</p>
            </div>
          </div>
          <div class="status-tag" :class="getStatusClass(user.status)">
            <div class="status-dot" :class="getStatusClass(user.status)"></div>
            <span>{{ getStatusText(user.status) }}</span>
          </div>
        </div>

        <div class="card-body">
          <div class="card-row">
            <div class="card-item">
              <span class="card-label">{{ $t('points') }}</span>
              <span class="card-value">{{ user.totalPoints || 0 }}</span>
            </div>
            <div class="card-item">
              <span class="card-label">{{ $t('programs_count') }}</span>
              <span class="card-value">{{ user.assignedPrograms || 0 }}</span>
            </div>
          </div>

          <div class="card-row">
            <div class="card-item">
              <span class="card-label">{{ $t('completion') }}</span>
              <div class="progress-wrapper-mobile">
                <div class="progress-bar">
                  <div
                      class="progress-fill"
                      :style="{ width: `${user.completionRate || 0}%` }"
                      :class="getProgressClass(user.completionRate)"
                  ></div>
                </div>
                <span class="progress-text">{{ Math.round(user.completionRate || 0) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { LottieAnimation } from 'lottie-web-vue'
import loadingAnim from '@/assets/animations/loading.json'

const { t } = useI18n()
const router = useRouter()

const props = defineProps({
  users: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Users'
  },
  loading: {
    type: Boolean,
    default: false
  },
  showViewAll: {
    type: Boolean,
    default: true
  },
  pageSize: {
    type: Number,
    default: 10
  },
  searchQuery: {
    type: String,
    default: ''
  },
  statusFilter: {
    type: String,
    default: 'all'
  }
})

const emit = defineEmits(['userSelect', 'viewAll', 'sortChange'])

const isExpanded = ref(false)
const sortField = ref('totalPoints')
const sortOrder = ref('desc')

const handleUserClick = (user) => {
  if (!user?.id) {
    return
  }

  router.push(`/users/detail/${user.id}`)
}

const filteredUsers = computed(() => {
  if (!Array.isArray(props.users)) {
    return []
  }

  let filtered = [...props.users]

  if (props.searchQuery && typeof props.searchQuery === 'string') {
    const query = props.searchQuery.toLowerCase().trim()
    if (query) {
      filtered = filtered.filter(user => {
        const name = String(user?.name ?? '').toLowerCase()
        const department = String(user?.department ?? '').toLowerCase()

        return name.includes(query) || department.includes(query)
      })
    }
  }

  if (props.statusFilter && props.statusFilter !== 'all') {
    filtered = filtered.filter(user => user?.status === props.statusFilter)
  }

  return filtered
})

const sortedUsers = computed(() => {
  if (!Array.isArray(filteredUsers.value)) {
    return []
  }

  const users = [...filteredUsers.value]

  users.sort((a, b) => {
    if (!a || !b) return 0

    let aVal = a[sortField.value]
    let bVal = b[sortField.value]

    if (aVal === null || aVal === undefined) aVal = ''
    if (bVal === null || bVal === undefined) bVal = ''

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = String(bVal).toLowerCase()
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortOrder.value === 'asc' ? aVal - bVal : bVal - aVal
    }

    if (sortOrder.value === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })

  return users
})

const displayedUsers = computed(() => {
  if (!Array.isArray(sortedUsers.value)) {
    return []
  }

  if (isExpanded.value || sortedUsers.value.length <= 3) {
    return sortedUsers.value
  }

  return sortedUsers.value.slice(0, 3)
})

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const sortBy = (field) => {
  if (!field || typeof field !== 'string') {
    return
  }

  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'desc'
  }

  emit('sortChange', { field: sortField.value, order: sortOrder.value })
}

const getSortClass = (field) => {
  if (!field || sortField.value !== field) return ''
  return `sort-active sort-${sortOrder.value}`
}

const getSortIconClass = (field) => {
  if (!field || sortField.value !== field) return 'sort-icon'
  return `sort-icon sort-icon-${sortOrder.value}`
}

const getStatusClass = (status) => {
  if (!status || typeof status !== 'string') {
    return 'Inactive'
  }

  const statusClasses = {
    'aktif': 'Active',
    'active': 'Active',
    'riskta': 'Risk',
    'risk': 'Risk',
    'pasif': 'Inactive',
    'inactive': 'Inactive'
  }

  return statusClasses[status.toLowerCase()] || 'Inactive'
}

const getStatusText = (status) => {
  if (!status || typeof status !== 'string') {
    return t('unknown')
  }

  const statusMap = {
    'aktif': 'active',
    'active': 'active',
    'riskta': 'at_risk',
    'risk': 'at_risk',
    'pasif': 'inactive',
    'inactive': 'inactive'
  }

  const key = statusMap[status.toLowerCase()] || 'unknown'
  return t(key)
}

const getProgressClass = (rate) => {
  const numRate = Number(rate) || 0

  if (numRate >= 80) return 'progress-excellent'
  if (numRate >= 60) return 'progress-good'
  if (numRate >= 40) return 'progress-moderate'
  return 'progress-low'
}

const getRank = (index) => {
  const numIndex = Number(index)

  if (isNaN(numIndex) || numIndex < 0) {
    return 1
  }

  return numIndex + 1
}

const getDefaultAvatar = (name) => {
  const safeName = String(name ?? 'User')
  const initial = safeName.trim() ? safeName.charAt(0).toUpperCase() : 'U'

  try {
    const svg = `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="40" rx="20" fill="#4367FF"/>
        <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="600" font-family="Arial">${initial}</text>
      </svg>
    `
    return `data:image/svg+xml;base64,${btoa(svg)}`
  } catch (error) {
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="40" rx="20" fill="#4367FF"/>
        <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="600" font-family="Arial">${initial}</text>
      </svg>
    `)
  }
}

const handleImageError = (event) => {
  if (!event?.target) {
    return
  }

  const userName = event.target.alt || 'User'
  event.target.src = getDefaultAvatar(userName)
}

watch(() => props.users, (newUsers) => {
  if (!Array.isArray(newUsers)) {
  }
  isExpanded.value = false
}, { deep: true })

watch(() => props.searchQuery, (newQuery) => {
  if (newQuery !== undefined && newQuery !== null) {
    isExpanded.value = false
  }
})

watch(() => props.statusFilter, (newFilter) => {
  if (newFilter !== undefined && newFilter !== null) {
    isExpanded.value = false
  }
})
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  overflow: hidden;
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 10px;
  background: transparent;
  margin-bottom: 5px;
}

.table-title {
  font-size: 16px;
  font-weight: 500;
  color: #101828;
}

.view-all-btn {
  color: #4367FF;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.view-all-btn:hover {
  background: #F0F4FF;
}

.desktop-table {
  overflow-x: auto;
  overflow-y: visible;
  transition: all 0.3s ease;
}

.desktop-table:not(.expanded) {
  max-height: fit-content;
}

.desktop-table.expanded {
  max-height: 80vh;
  overflow-y: auto;
}

.content-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.header-table {
  background: #F9FAFB;
  position: sticky;
  top: 0;
  z-index: 10;
}

.trhead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: #475467;
  border-bottom: 1px solid #EAECF0;
  cursor: pointer;
  position: relative;
  font-family: 'euclid_semi_bold', 'sans-serif';
  user-select: none;
}

.rank-header {
  cursor: default !important;
  width: 60px;
}

.trhead th:hover:not(.rank-header) {
  background: #F2F4F7;
}

.sort-icon {
  margin-left: 4px;
  transition: all 0.2s;
  color: #9CA3AF;
}

.sort-active .sort-icon {
  color: #4367FF;
}

.sort-icon-asc {
  transform: rotate(180deg);
}

.sort-icon-desc {
  transform: rotate(0deg);
}

.tbody-container {
  background: white;
}

.tr-class {
  cursor: pointer;
  transition: background-color 0.15s;
}

.tr-class:hover {
  background: #F8F9FA;
}

.td-style {
  padding: 12px 16px;
  border-bottom: 1px solid #F2F4F7;
  vertical-align: middle;
  text-align: left;
}

.rank-cell {
  width: 60px;
  text-align: center;
}

.rank-number {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  display: flex;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #eaecf0;
  line-height: 24px;
  text-align: center;
  justify-content: center;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #F9FAFB;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.user-avatar:hover {
  opacity: 0.8;
}

.user-details {
  min-width: 0;
  overflow: hidden;
}

.user-name {
  font-weight: 500;
  color: #101828;
  font-size: 14px;
  display: block;
  margin-bottom: 2px;
  cursor: pointer;
  transition: color 0.2s;
}

.user-name:hover {
  color: #4367FF;
}

.user-department {
  font-size: 12px;
  color: #667085;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.points-wrapper {
  display: flex;
  align-items: center;
  width: 100px;
  height: 24px;
  background: #ebedfb;
  justify-content: center;
  border-radius: 5px;
}

.points-text {
  font-size: 12px;
  font-family: euclid_semi_bold, sans-serif;
  color: #000000;
}

.status-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #040c2d;
  border-radius: 8px;
  padding: 6px 12px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  justify-content: flex-start;
  min-width: 80px;
}

.status-dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-tag.Active {
  background-color: #ddfbde;
}

.status-tag.Risk {
  background-color: #fff4e6;
}

.status-tag.Inactive {
  background-color: #fad9c6;
}

.status-dot.Active {
  background-color: #3da33e;
}

.status-dot.Risk {
  background-color: #f59e0b;
}

.status-dot.Inactive {
  background-color: #f09460;
}

.programs-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.test-class {
  font-family: euclid_semi_bold, sans-serif;
  font-size: 12px;
  color: #667085;
}

.progress-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar {
  width: 60px;
  height: 6px;
  background: #F2F4F7;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-excellent {
  background: #22C55E;
}

.progress-good {
  background: #4367FF;
}

.progress-moderate {
  background: #F59E0B;
}

.progress-low {
  background: #EF4444;
}

.progress-text {
  font-size: 11px;
  font-weight: 500;
  color: #475467;
  min-width: 32px;
}

.table-loading {
  padding: 60px 20px;
  text-align: center;
}

.loading-content, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #667085;
}

.lottie-loader {
  width: 80px;
  height: 80px;
}

.text {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
}

/* Mobile Cards */
.mobile-cards {
  display: none;
}

.mobile-loading, .mobile-empty {
  padding: 40px 20px;
  text-align: center;
}

.user-card {
  background: #FAFBFC;
  border: 1px solid #E4E7EC;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.user-card:hover {
  background: #F2F4F7;
  border-color: #4367FF;
}

.user-card:last-child {
  margin-bottom: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 12px;
}

.card-header .user-info {
  flex: 1;
  min-width: 0;
  position: relative;
}

.rank-badge {
  position: absolute;
  left: -8px;
  top: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #4367FF;
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-header .user-details {
  min-width: 0;
}

.card-header .user-name {
  font-size: 15px;
  font-weight: 600;
  color: #101828;
  margin-bottom: 2px;
}

.card-header .user-department {
  font-size: 12px;
  color: #667085;
  max-width: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.card-header .status-tag {
  flex-shrink: 0;
  font-size: 11px;
  padding: 4px 8px;
  min-width: auto;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.card-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-label {
  font-size: 11px;
  color: #667085;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-value {
  font-size: 14px;
  color: #101828;
  font-weight: 600;
}

.progress-wrapper-mobile {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-wrapper-mobile .progress-bar {
  flex: 1;
  min-width: 40px;
}

.progress-wrapper-mobile .progress-text {
  font-size: 12px;
  font-weight: 600;
  color: #101828;
  min-width: 28px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .user-department {
    max-width: 120px;
  }

  .progress-bar {
    width: 50px;
  }
}

@media (max-width: 768px) {
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .table-header {
    padding: 0;
    margin-bottom: 16px;
  }

  .table-title {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .table-container {
    padding: 8px;
  }

  .user-card {
    padding: 12px;
  }

  .card-row {
    gap: 12px;
  }

  .card-item {
    gap: 2px;
  }

  .card-label {
    font-size: 10px;
  }

  .card-value {
    font-size: 13px;
  }
}
</style>
